import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/useToast'
import { Loader2, Shield, Copy, Check, AlertTriangle, RefreshCw } from 'lucide-react'

interface MfaSetupProps {
  onComplete?: () => void
  onCancel?: () => void
  mandatory?: boolean
}

export function MfaSetup({ onComplete, onCancel, mandatory = false }: MfaSetupProps) {
  const [step, setStep] = useState<'qr' | 'verify'>('qr')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [factorId, setFactorId] = useState('')
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isEnrolling, setIsEnrolling] = useState(true)
  const [enrollError, setEnrollError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const { enrollMfa, verifyMfaEnrollment } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const enrollingRef = useRef(false)

  const initEnrollment = async () => {
    if (enrollingRef.current) return
    enrollingRef.current = true

    setIsEnrolling(true)
    setEnrollError(null)

    try {
      const result = await enrollMfa()

      if ('error' in result) {
        if (mandatory) {
          setEnrollError(result.error.message)
          setIsEnrolling(false)
          return
        }
        toast({
          title: 'Enrollment Failed',
          description: result.error.message,
          variant: 'destructive',
        })
        if (onCancel) {
          onCancel()
        } else {
          navigate('/dashboard')
        }
        return
      }

      setQrCode(result.qrCode)
      setSecret(result.secret)
      setFactorId(result.factorId)
      setIsEnrolling(false)
    } finally {
      enrollingRef.current = false
    }
  }

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (cancelled) return
      await initEnrollment()
    }
    run()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCopySecret = async () => {
    await navigator.clipboard.writeText(secret)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    const { error } = await verifyMfaEnrollment(code, factorId)

    if (error) {
      toast({
        title: 'Verification Failed',
        description: error.message,
        variant: 'destructive',
      })
      setIsLoading(false)
      return
    }

    toast({
      title: '2FA Enabled',
      description: 'Two-factor authentication is now active',
    })

    if (onComplete) {
      onComplete()
    } else {
      navigate('/dashboard')
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/dashboard')
    }
  }

  if (isEnrolling) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-brand-fg mb-4" />
        <p className="text-muted-foreground">Setting up 2FA...</p>
      </div>
    )
  }

  if (enrollError) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-4">
        <AlertTriangle className="h-8 w-8 text-amber-500" />
        <div className="text-center">
          <p className="font-medium">2FA Setup Failed</p>
          <p className="text-sm text-muted-foreground mt-1">{enrollError}</p>
        </div>
        <Button onClick={initEnrollment} className="bg-dextoro text-black hover:bg-dextoro/90">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry Setup
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-center">
        <div className="p-3 rounded-xl bg-brand-fg/10">
          <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-brand-fg" />
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-lg sm:text-xl font-semibold">Set Up Two-Factor Authentication</h2>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
          {step === 'qr' ? 'Scan the QR code with your authenticator app' : 'Enter the verification code'}
        </p>
      </div>

      {step === 'qr' ? (
        <div className="space-y-4">
          <div className="flex justify-center">
            <div className="p-3 sm:p-4 bg-white rounded-lg">
              <img src={qrCode} alt="QR Code" className="w-40 h-40 sm:w-48 sm:h-48" />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-center text-muted-foreground">
              Or enter this code manually:
            </p>
            <div className="flex items-center gap-2 p-2 sm:p-3 bg-muted rounded-lg">
              <code className="flex-1 text-[10px] sm:text-xs font-mono break-all">{secret}</code>
              <Button variant="ghost" size="sm" onClick={handleCopySecret} className="min-h-[44px] min-w-[44px] p-0 flex items-center justify-center">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <Button
            onClick={() => setStep('verify')}
            className="w-full bg-dextoro text-black hover:bg-dextoro/90"
          >
            Continue
          </Button>

          {!mandatory && (
            <Button variant="ghost" onClick={handleCancel} className="w-full">
              Cancel Setup
            </Button>
          )}
        </div>
      ) : (
        <form onSubmit={handleVerify} className="space-y-4">
          <div className="flex justify-center gap-1.5 sm:gap-2">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="w-9 h-11 sm:w-10 sm:h-12 border rounded-lg flex items-center justify-center text-base sm:text-lg font-mono bg-muted/50"
              >
                {code[i] || ''}
              </div>
            ))}
          </div>

          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={code}
            onChange={handleCodeChange}
            className="sr-only"
            autoFocus
            autoComplete="one-time-code"
          />

          <Button
            type="submit"
            className="w-full bg-dextoro text-black hover:bg-dextoro/90"
            disabled={isLoading || code.length !== 6}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify & Enable'
            )}
          </Button>

          <Button variant="ghost" onClick={() => setStep('qr')} className="w-full">
            Back to QR Code
          </Button>
        </form>
      )}
    </div>
  )
}
